// Prevent re-entry if kick is already in progress
let kickInProgress = false

/**
 * Dispatch a global event that signals the user's session was revoked by another login.
 */
export function dispatchUserKick () {
  window.dispatchEvent(new CustomEvent('user-session-kicked'))
}

/**
 * Dispatch a global event that signals the user's session expired naturally (idle timeout).
 */
export function dispatchSessionExpired () {
  window.dispatchEvent(new CustomEvent('user-session-expired'))
}

/**
 * Full cleanup + redirect for a kicked user.
 * Navigates FIRST (while token still exists so the router guard doesn't interfere),
 * then clears localStorage after navigation settles.
 * @param {import('vue-router').Router} router
 */
export async function kickUser (router) {
  if (kickInProgress) return
  kickInProgress = true

  try {
    // Navigate first — token still in localStorage so guard passes cleanly
    await router.replace('/user/kicked')
  } catch (_) {
    // NavigationDuplicated or similar — already on kicked screen
  } finally {
    // Wipe local auth state
    localStorage.removeItem('auth_token')
    localStorage.removeItem('token')
    localStorage.removeItem('user_data')
    localStorage.removeItem('user')
    localStorage.removeItem('user_session_key')

    kickInProgress = false
  }
}

/**
 * Redirect to login for a naturally-expired session (no kicked screen).
 * @param {import('vue-router').Router} router
 */
export async function expireUser (router) {
  if (kickInProgress) return
  kickInProgress = true

  try {
    await router.replace('/login')
  } catch (_) {
    // already on login
  } finally {
    localStorage.removeItem('auth_token')
    localStorage.removeItem('token')
    localStorage.removeItem('user_data')
    localStorage.removeItem('user')
    localStorage.removeItem('user_session_key')

    kickInProgress = false
  }
}

/**
 * Send a single poll to /check-session.
 * Returns true if session is still active, false if it should be terminated.
 * Throws on network failure (caller should swallow and skip).
 */
async function pollSession () {
  const token = localStorage.getItem('auth_token')
  const sessionKey = localStorage.getItem('user_session_key')

  if (!token || !sessionKey) return

  const res = await fetch(
    `https://api.quali-d.com/check-session?session_key=${encodeURIComponent(sessionKey)}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json'
      }
    }
  )

  if (!res.ok) return // Network error — skip this poll cycle, don't kick

  const data = await res.json()

  if (data.active === false) {
    if (data.reason === 'expired') {
      dispatchSessionExpired()
    } else {
      dispatchUserKick()
    }
  }
}

/**
 * Start polling the backend every 30 seconds to verify the session is still active.
 * Also fires immediately when the browser tab becomes visible again (Page Visibility API)
 * to refresh the session after device sleep or tab switching — preventing false kicks.
 *
 * @returns {Function} Cleanup function — call it on component unmount.
 */
export function startSessionPolling () {
  const poll = async () => {
    try {
      await pollSession()
    } catch (_) {
      // Network error — skip this poll cycle, don't kick on connectivity issues
    }
  }

  // Fire a poll immediately when the user returns to this tab
  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') poll()
  }
  document.addEventListener('visibilitychange', onVisibilityChange)

  const interval = setInterval(poll, 30000)

  // Return a cleanup function so the caller doesn't need to know implementation details
  return () => {
    clearInterval(interval)
    document.removeEventListener('visibilitychange', onVisibilityChange)
  }
}
